import {
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import { Button, type ButtonProps } from '@/components/ui/button';

import { Checkbox } from './Checkbox';
import { SearchBar } from './SearchBar';

export type Severity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'info'
  | 'exposure';

export interface DropdownOption {
  id?: string | number;
  severity?: Severity;
  label: string;
  count?: number | string;
  value?: string | number;
  icon?: React.ReactNode;
  onClick?: () => void;
  isDivider?: boolean;
  disabled?: boolean;
  checked?: boolean;
  isGroup?: boolean;
  items?: DropdownOption[];
  isExpanded?: boolean;
  groupId?: string;
  groupItems?: DropdownOption[];
  tooltip?: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const sizeClasses = {
  xs: 'py-1 px-2 text-xs',
  sm: 'py-1.5 px-3 text-sm',
  md: 'py-2 px-4 text-sm',
  lg: 'py-2.5 px-5 text-base',
  xl: 'py-3 px-6 text-base',
} as const;

// eslint-disable-next-line react-refresh/only-export-components
export const iconSizes = {
  xs: 'size-3',
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-4',
  xl: 'size-5',
} as const;

// Constants for virtualization
const ROW_HEIGHT = 50; // Height of each dropdown item in pixels
const MAX_DROPDOWN_HEIGHT = 600; // Maximum height of the dropdown
const VIEWPORT_BUFFER = 40; // Buffer space from viewport bottom

type DropdownStyleType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'rounded'
  | 'icon'
  | 'select';

const styleTypeToVariant = (
  styleType: DropdownStyleType
): ButtonProps['variant'] => {
  switch (styleType) {
    case 'primary':
      return 'default';
    case 'secondary':
      return 'secondary';
    case 'tertiary':
    case 'rounded':
    case 'icon':
      return 'ghost';
    case 'select':
      return 'outline';
  }
};

interface DropdownTriggerProps {
  selected?: DropdownOption[];
  onClick?: () => void;
  className?: string;
  size?: keyof typeof sizeClasses;
  isOpen?: boolean;
  placeholder?: string;
  styleType?: DropdownStyleType;
  icon?: React.ReactNode;
  multiSelect?: boolean;
}

export const DropdownTrigger: React.FC<DropdownTriggerProps> = ({
  selected,
  onClick,
  className,
  size = 'md',
  isOpen = false,
  placeholder: placeholderProp = 'Select an option',
  styleType = 'tertiary',
  icon,
  multiSelect = false,
}) => {
  const placeholder = styleType === 'icon' ? '' : placeholderProp;

  // Helper function to get display label
  const getDisplayLabel = (): string => {
    if (!selected || selected.length === 0) return placeholder;

    // First check for complete groups
    const completeGroups = selected.filter(
      item =>
        item.isGroup &&
        item.items?.every(groupItem =>
          selected.some(s => s.id === groupItem.id)
        )
    );

    // Get remaining individual items (not part of complete groups)
    const individualItems = selected.filter(
      item =>
        !item.isGroup &&
        !completeGroups.some(group =>
          group.items?.some(groupItem => groupItem.id === item.id)
        )
    );

    const labels: string[] = [
      ...completeGroups.map(group => group.label),
      ...individualItems.map(item => item.label),
    ];

    if (labels.length > 0) {
      return labels.join(', ');
    }

    return multiSelect
      ? `${selected.length.toString()} selected`
      : selected[0].label;
  };

  const label = getDisplayLabel();

  const startIcon =
    icon ??
    (!multiSelect &&
      selected &&
      selected.length > 0 &&
      selected[0].severity && (
        <div
          className={twMerge(
            'h-2.5 w-2.5 rounded-full border',
            `bg-chariot-status-${selected[0].severity}`,
            `border-chariot-stroke-${selected[0].severity}`
          )}
        />
      ));

  return (
    <Button
      onClick={() => {
        onClick?.();
      }}
      variant={styleTypeToVariant(styleType)}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      className={twMerge(
        sizeClasses[size],
        isOpen &&
          'border-chariot-brand-primary bg-chariot-background-secondary',
        styleType === 'select' &&
          'border-chariot-stroke bg-chariot-background-primary hover:bg-chariot-background-secondary w-full justify-between rounded-[2px] font-normal',
        className
      )}
    >
      {startIcon && <span>{startIcon}</span>}
      <span
        className={twMerge(
          'truncate',
          (!selected || selected.length === 0) && 'text-chariot-text-secondary'
        )}
      >
        {label}
      </span>
      {styleType !== 'icon' && (
        <ChevronDownIcon
          className={twMerge(
            'stroke-chariot-text-primary stroke-[1.5px]',
            iconSizes[size]
          )}
        />
      )}
    </Button>
  );
};

interface DropdownListProps {
  border?: boolean;
  options: DropdownOption[];
  onSelect?: (option: DropdownOption) => void;
  onToggleCheckbox?: (option: DropdownOption) => void;
  className?: string;
  style?: React.CSSProperties;
  virtualized?: boolean;
  title?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  multiSelect?: boolean;
  onApply?: () => void;
  isPraetorianUser?: boolean;
  fitWidth?: boolean;
  isLoadingOptions?: boolean;
}

export const DropdownList: React.FC<DropdownListProps> = ({
  border = true,
  options,
  onSelect,
  onToggleCheckbox,
  className,
  style,
  virtualized = false,
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  multiSelect = false,
  onApply,
  isPraetorianUser = false,
  fitWidth = false,
  isLoadingOptions = false,
}) => {
  const maxHeightRef = useRef(MAX_DROPDOWN_HEIGHT);
  const contentRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Optimize virtualizer configuration
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => contentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    paddingStart: 4,
    paddingEnd: 4,
  });

  // Calculate available space for the dropdown
  useEffect(() => {
    const calculateMaxHeight = (): void => {
      if (!parentRef.current) return;

      const windowHeight = window.innerHeight;
      const dropdownTop = parentRef.current.getBoundingClientRect().top;
      const maxAllowedHeight = windowHeight - dropdownTop - VIEWPORT_BUFFER;
      maxHeightRef.current = Math.min(MAX_DROPDOWN_HEIGHT, maxAllowedHeight);
    };

    calculateMaxHeight();
    window.addEventListener('resize', calculateMaxHeight);
    return () => {
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, []);

  const cloneIconWithClasses = (
    icon: React.ReactNode,
    disabled?: boolean
  ): React.ReactNode => {
    if (!React.isValidElement(icon)) return icon;

    return React.cloneElement(
      icon as React.ReactElement<{ className?: string }>,
      {
        className: twMerge(
          'size-5 inline-flex justify-center items-center mr-1.5',
          disabled
            ? 'text-chariot-text-disabled'
            : 'text-chariot-text-secondary'
        ),
      }
    );
  };

  const renderOption = (
    option: DropdownOption,
    index: number,
    hideBorder?: boolean
  ): React.JSX.Element => {
    if (option.isDivider) {
      return <div className="border-chariot-stroke my-1 h-px border-t" />;
    }

    const isExpanded =
      option.isGroup &&
      expandedGroups.has(option.id?.toString() ?? option.label);

    return (
      <div
        className={twMerge(
          'w-full pb-1',
          border && !hideBorder && 'border-chariot-stroke border-b'
        )}
      >
        <div
          className={twMerge(
            'hover:bg-chariot-background-secondary flex items-center gap-3 rounded-md px-2 py-2',
            option.disabled &&
              'hover:bg-chariot-background-primary cursor-default'
          )}
          title={option.tooltip ?? undefined}
        >
          {multiSelect ? (
            <div className="shrink-0 items-center">
              <button
                onClick={e => {
                  e.stopPropagation();
                  onToggleCheckbox?.(option);
                }}
                className="flex items-center justify-center"
                aria-checked={option.checked}
                role="checkbox"
              >
                <Checkbox
                  isChecked={option.checked ?? false}
                  disabled={option.disabled}
                />
              </button>
            </div>
          ) : (
            option.severity && (
              <div
                className={twMerge(
                  'size-4 shrink-0 rounded-xs border',
                  `bg-chariot-status-${option.severity}`,
                  `border-chariot-stroke-${option.severity}`
                )}
              />
            )
          )}

          <div
            className={twMerge(
              'flex min-w-0 flex-1 items-center justify-between',
              !option.disabled && 'cursor-pointer'
            )}
            onClick={e => {
              e.stopPropagation();
              if (!option.disabled) {
                if (multiSelect) {
                  onToggleCheckbox?.(option);
                } else {
                  option.onClick?.();
                  onSelect?.(option);
                }
              }
            }}
          >
            <p
              className={twMerge(
                'flex min-w-0 flex-1 items-center font-medium',
                option.disabled
                  ? 'text-chariot-text-disabled'
                  : 'text-chariot-text-primary'
              )}
            >
              {option.icon &&
                cloneIconWithClasses(option.icon, option.disabled)}
              <span className="truncate">{option.label}</span>
            </p>
            <div className="flex items-center gap-2">
              {option.count !== undefined && (
                <span className="text-chariot-text-primary shrink-0 text-base">
                  {option.count.toLocaleString()}
                </span>
              )}
              {option.tooltip && (
                <InformationCircleIcon className="text-chariot-text-secondary size-4 shrink-0" />
              )}
              {option.isGroup && isPraetorianUser && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    toggleGroup(option.id?.toString() ?? option.label);
                  }}
                  className="hover:bg-chariot-background-secondary shrink-0 rounded p-1"
                >
                  <ChevronDownIcon
                    className={twMerge(
                      'text-chariot-text-secondary size-4 transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
        {option.isGroup && isPraetorianUser && isExpanded && option.items && (
          <div className="mt-1 ml-2 space-y-1">
            {option.items.map((item, itemIndex) => (
              <div key={item.id ?? itemIndex}>
                {renderOption(item, itemIndex, true)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Calculate minimum height
  const getMinHeight = (): number => {
    let minHeight = 0;
    if (title) minHeight += 40;
    if (onSearchChange !== undefined) minHeight += 60;
    if (multiSelect) minHeight += 70;
    return minHeight;
  };

  // Structure for virtualized content
  const renderVirtualizedContent = () => (
    <div ref={contentRef} className="flex-1 overflow-auto px-4 py-2">
      {isLoadingOptions ? (
        <p className="text-chariot-text-secondary min-h-36 py-2 text-center text-sm">
          Loading options...
        </p>
      ) : options.length === 0 ? (
        <p className="text-chariot-text-secondary min-h-36 py-2 text-center text-sm">
          No results found
        </p>
      ) : (
        <div
          className="relative w-full"
          style={{
            height: `${virtualizer.getTotalSize().toString()}px`, // Explicit pixel height
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map(virtualRow => (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              className="absolute top-0 left-0 w-full"
              style={{
                height: `${virtualRow.size.toString()}px`,
                transform: `translateY(${virtualRow.start.toString()}px)`,
              }}
            >
              {renderOption(options[virtualRow.index], virtualRow.index)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Structure for non-virtualized content
  const renderStandardContent = () => (
    <div className="flex-1 overflow-auto px-4 py-2">
      {isLoadingOptions ? (
        <p className="text-chariot-text-secondary min-h-36 py-2 text-center text-sm">
          Loading options...
        </p>
      ) : options.length === 0 ? (
        <p className="text-chariot-text-secondary min-h-36 py-2 text-center text-sm">
          No results found
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {options.map((option, index) => (
            <div key={option.id?.toString() ?? option.label}>
              {renderOption(option, index)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={parentRef}
      className={twMerge(
        'animate-in fade-in duration-200',
        fitWidth ? 'w-fit' : virtualized ? 'w-[280px]' : 'w-[400px]',
        className
      )}
      style={{
        ...style,
        display: 'flex',
        flexDirection: 'column',
        height: virtualized
          ? Math.max(
              getMinHeight(),
              Math.min(virtualizer.getTotalSize() + 140, maxHeightRef.current)
            )
          : undefined,
        maxHeight: !virtualized ? maxHeightRef.current : undefined,
        minHeight: getMinHeight(),
      }}
      data-testid="dropdown-list"
    >
      {/* Header */}
      <div className="flex-none">
        {title && (
          <div className="px-4 pt-4">
            <h3 className="text-chariot-text-secondary text-xs">{title}</h3>
          </div>
        )}

        {onSearchChange && (
          <div className="px-4 pt-2">
            <SearchBar
              value={searchValue}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Content - conditionally render based on virtualization flag */}
      {virtualized ? renderVirtualizedContent() : renderStandardContent()}

      {/* Apply button */}
      {multiSelect && (
        <div className="border-chariot-stroke mt-auto flex-none border-t">
          <div className="px-4 py-3">
            <Button
              onClick={() => onApply?.()}
              variant="default"
              className="w-full"
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface DropdownProps {
  options: DropdownOption[];
  selected?: DropdownOption[];
  onSelect?: (option: DropdownOption) => void;
  className?: string;
  size?: keyof typeof sizeClasses;
  triggerClassName?: string;
  listClassName?: string;
  placeholder?: string;
  styleType?: DropdownStyleType;
  virtualized?: boolean;
  title?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  icon?: React.ReactNode;
  border?: boolean;
  multiSelect?: boolean;
  onApply?: (selectedOptions: DropdownOption[]) => void;
  position?: 'left' | 'right';
  isPraetorianUser?: boolean;
  disablePortal?: boolean;
  fitWidth?: boolean;
  isLoadingOptions?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
  options: initialOptions = [],
  selected,
  onSelect,
  className,
  size = 'md',
  triggerClassName,
  listClassName,
  placeholder,
  styleType = 'tertiary',
  virtualized = false,
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  icon,
  border,
  multiSelect = false,
  onApply,
  position = 'left',
  isPraetorianUser = false,
  disablePortal = false,
  fitWidth,
  isLoadingOptions = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [buttonPosition, setButtonPosition] = useState<{
    top: number;
    left?: number;
    width?: number;
    right?: number;
  }>();
  const [dropdownOptions, setDropdownOptions] =
    useState<DropdownOption[]>(initialOptions);

  // Update options when initialOptions changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDropdownOptions(
      initialOptions.map((opt, index) => ({
        ...opt,
        // Ensure every option has an ID for internal tracking and comparison
        id: opt.id ?? `internal-dropdown-id-${index.toString()}`,
        ...(opt.items && {
          items: opt.items.map((item, itemIndex) => ({
            ...item,
            id:
              item.id ??
              `internal-dropdown-id-group-${index.toString()}-item-${itemIndex.toString()}`,
          })),
        }),
      }))
    );
  }, [initialOptions]);

  // Update button position when dropdown opens or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current && !disablePortal) {
        const rect = triggerRef.current.getBoundingClientRect();

        // Use viewport-relative coordinates for fixed positioning
        setButtonPosition({
          top: rect.bottom,
          left: position === 'left' ? rect.left : undefined,
          width: fitWidth ? undefined : virtualized ? 280 : 400,
          right:
            position === 'right' ? window.innerWidth - rect.right : undefined,
        });
      }
    };

    if (isOpen && !disablePortal) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setButtonPosition(undefined);
    }
  }, [isOpen, position, fitWidth, virtualized, disablePortal]);

  const [selectedItems, setSelectedItems] = useState<DropdownOption[]>(
    selected ?? []
  );

  // Helper function to get unique items by ID
  const getUniqueById = (items: DropdownOption[]): DropdownOption[] => {
    const seen = new Set<string>();
    return items.filter(item => {
      const itemId = item.id?.toString();
      if (!itemId) return true;
      if (seen.has(itemId)) return false;
      seen.add(itemId);
      return true;
    });
  };

  // Memoize options mapping to prevent unnecessary recalculations
  const options = React.useMemo(
    () =>
      dropdownOptions.map(option => {
        if (option.isGroup && option.items) {
          // For groups, check if any items are selected
          const hasSelectedItems = option.items.some(item =>
            selectedItems.some(s => s.id === item.id)
          );

          return {
            ...option,
            checked: hasSelectedItems,
            items: option.items.map(item => ({
              ...item,
              checked: selectedItems.some(s => s.id === item.id),
            })),
          };
        }

        return {
          ...option,
          checked: selectedItems.some(s => s.id === option.id),
        };
      }),
    [dropdownOptions, selectedItems]
  );

  const handleToggleCheckbox = React.useCallback(
    (option: DropdownOption): void => {
      if (!multiSelect) return;

      setSelectedItems(prev => {
        let newItems: DropdownOption[];

        if (option.isGroup && option.items) {
          const allSelected = option.items.every(item =>
            prev.some(s => s.id === item.id)
          );

          if (allSelected) {
            // If all items were selected, remove them and the group
            newItems = prev.filter(
              item =>
                !option.items?.some(groupItem => groupItem.id === item.id) &&
                item.id !== option.id
            );
          } else {
            // If not all selected, add all items and the group
            newItems = [
              ...prev.filter(
                item =>
                  !option.items?.some(groupItem => groupItem.id === item.id) &&
                  item.id !== option.id
              ),
              option,
              ...option.items,
            ];
          }
        } else {
          // For individual items
          const isSelected = prev.some(item => item.id === option.id);
          if (isSelected) {
            // Remove the item
            newItems = prev.filter(item => item.id !== option.id);

            // Also remove any groups that were complete but now aren't
            newItems = newItems.filter(item => {
              if (item.isGroup && item.items) {
                return item.items.every(groupItem =>
                  newItems.some(s => s.id === groupItem.id)
                );
              }
              return true;
            });
          } else {
            // Add the item
            newItems = [...prev, option];

            // Check if this completes any groups and add them if so
            options.forEach(group => {
              if (group.isGroup && group.items) {
                const groupComplete = group.items.every(
                  groupItem =>
                    groupItem.id === option.id ||
                    newItems.some(s => s.id === groupItem.id)
                );
                if (
                  groupComplete &&
                  !newItems.some(item => item.id === group.id)
                ) {
                  newItems.push(group);
                }
              }
            });
          }
        }

        return getUniqueById(newItems);
      });
    },
    [multiSelect, options]
  );

  // Handle apply button click
  const handleApply = () => {
    const uniqueItems = getUniqueById(selectedItems);
    onApply?.(uniqueItems);
    setIsOpen(false);
  };

  // Update selectedItems when selected prop changes
  useEffect(() => {
    if (selected) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedItems(getUniqueById(selected));
    }
  }, [selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleSelect = (option: DropdownOption): void => {
    // Check if this is an action item (has onClick handler)
    const isActionItem = typeof option.onClick === 'function';

    // For action items, don't update the selection state
    if (!isActionItem && !multiSelect) {
      onSelect?.(option);
      setSelectedItems([option]);
    }

    // Always close the dropdown after selection
    setIsOpen(false);
  };

  const handleTriggerClick = (): void => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      className={twMerge(
        'relative inline-block',
        disablePortal && 'overflow-visible',
        className
      )}
      ref={triggerRef}
    >
      <DropdownTrigger
        selected={selectedItems}
        onClick={handleTriggerClick}
        size={size}
        isOpen={isOpen}
        className={triggerClassName}
        placeholder={placeholder}
        styleType={styleType}
        icon={icon}
        multiSelect={multiSelect}
      />

      {isOpen &&
        (disablePortal ? (
          <div
            ref={dropdownRef}
            className="absolute top-full z-50 mt-2"
            style={{
              left: position === 'left' ? '0' : undefined,
              right: position === 'right' ? '0' : undefined,
              width: fitWidth ? 'max-content' : virtualized ? '280px' : '400px',
            }}
          >
            <DropdownList
              options={options}
              onSelect={handleSelect}
              onToggleCheckbox={handleToggleCheckbox}
              className={twMerge(
                'bg-chariot-background-primary border-chariot-brand-primary rounded border shadow-lg',
                listClassName
              )}
              virtualized={virtualized}
              title={title}
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              searchPlaceholder={searchPlaceholder}
              border={border}
              multiSelect={multiSelect}
              onApply={handleApply}
              isPraetorianUser={isPraetorianUser}
              fitWidth={fitWidth}
              isLoadingOptions={isLoadingOptions}
            />
          </div>
        ) : (
          buttonPosition &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed top-0 left-0 z-9999 h-0 w-full overflow-visible"
              style={{ pointerEvents: 'none' }}
            >
              <div
                className="fixed inset-0 z-40"
                style={{ pointerEvents: 'auto' }}
                onClick={() => {
                  setIsOpen(false);
                }}
              />
              <div
                className="absolute z-50"
                style={{
                  position: 'fixed',
                  top: buttonPosition.top + 8,
                  left: position === 'left' ? buttonPosition.left : undefined,
                  right: buttonPosition.right,
                  width: fitWidth ? 'max-content' : virtualized ? 280 : 400,
                  pointerEvents: 'auto',
                }}
              >
                <DropdownList
                  options={options}
                  onSelect={handleSelect}
                  onToggleCheckbox={handleToggleCheckbox}
                  className={twMerge(
                    'bg-chariot-background-primary border-chariot-brand-primary mt-2 rounded border shadow-lg',
                    listClassName
                  )}
                  virtualized={virtualized}
                  title={title}
                  searchValue={searchValue}
                  onSearchChange={onSearchChange}
                  searchPlaceholder={searchPlaceholder}
                  border={border}
                  multiSelect={multiSelect}
                  onApply={handleApply}
                  isPraetorianUser={isPraetorianUser}
                  fitWidth={fitWidth}
                  isLoadingOptions={isLoadingOptions}
                />
              </div>
            </div>,
            document.body
          )
        ))}
    </div>
  );
};
